import GoogleIcon from '@assets/icons/google.svg?react';
import IconHide from '@assets/icons/hide.svg?react';
import MicrosoftIcon from '@assets/icons/microsoft.svg?react';
import IconShow from '@assets/icons/show.svg?react';
import {
 Alert,
 Box,
 Button,
 Checkbox,
 FormControlLabel,
 IconButton,
 Link as LinkMui,
 MenuItem,
 Select,
 TextField,
 Theme,
 Typography,
} from '@mui/material';
import { TwoFAMethod } from '@redux/identity/identityTypes';
import { useLoginForm } from './useLoginForm';

const LoginForm = () => {
 const {
  passwordVisible,
  step,
  errorMessage,
  redirectedFromReset,
  selectedMethod,
  code,
  codeGenerated,
  emailRegister,
  passwordRegister,
  handleFormSubmit,
  watch,
  errors,
  loading,
  languages,
  i18n,
  t,
  handleEmailChange,
  handlePasswordChange,
  handleMethodChange,
  handleCodeChange,
  handleGenerateCode,
  handle2FASubmit,
  handleBackToLogin,
  handleTogglePasswordVisibility,
  handleLanguageChange,
  handleForgotPasswordClick,
  forgotEmail,
  forgotPasswordSubmittedEmail,
  forgotPasswordLoading,
  handleForgotEmailChange,
  availableMethods,
  handleLoginWith,
  handleDevLogin,
 } = useLoginForm();

 const showForgotPasswordEmailSentInfo =
  forgotPasswordSubmittedEmail.length > 0;
 const compactLoginDescriptionMargin =
  redirectedFromReset || showForgotPasswordEmailSentInfo;

 return (
  <Box
   component="form"
   onSubmit={handleFormSubmit}
   sx={{
    width: { xs: '100%', md: '90%', lg: '80%', xl: '60%' },
    border: (theme: Theme) => `1px solid ${theme.palette.kemek.lightestGray}`,
    borderRadius: '4px',
    margin: { xs: '0 auto 16px auto', md: '0 auto' },
    p: { xs: '20px', md: 5 },
   }}
  >
   <Box
    sx={{
     width: '100%',
     display: 'flex',
     justifyContent: 'flex-end',
     mb: 1,
    }}
   >
    <TextField
     select
     size="small"
     value={i18n.language}
     onChange={(event) => handleLanguageChange(event.target.value)}
     sx={{ minWidth: 80 }}
    >
     {Object.keys(languages).map((language) => (
      <MenuItem key={language} value={language}>
       {language.toUpperCase()}
      </MenuItem>
     ))}
    </TextField>
   </Box>
   {step === 1 && (
    <Box>
     <Typography sx={{ fontSize: 18, mb: '12px' }}>
      {t('login.twoFactorTitle')}
     </Typography>
     <Typography sx={{ fontSize: 14, mb: '28px' }}>
      {t('login.twoFactorDescription')}
     </Typography>
     <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
      {availableMethods.length > 0 && (
       <Select
        value={selectedMethod}
        onChange={(e) => handleMethodChange(e.target.value as TwoFAMethod)}
        displayEmpty
        sx={{
         width: '100%',
        }}
       >
        <MenuItem value="" disabled>
         {t('login.select2FAMethod')}
        </MenuItem>
        {availableMethods.map(({ method, label }) => (
         <MenuItem key={method} value={method}>
          {label}
         </MenuItem>
        ))}
       </Select>
      )}
      {selectedMethod === 3 && (
       <TextField
        label={t('login.verificationCode')}
        value={code}
        onChange={handleCodeChange}
        sx={{
         width: '100%',
         mb: '12px',
        }}
        slotProps={{
         inputLabel: {
          shrink: code.length > 0,
         },
        }}
       />
      )}
     </Box>
     {selectedMethod && (selectedMethod === 1 || selectedMethod === 2) && (
      <>
       {!codeGenerated ? (
        <Button
         variant="contained"
         onClick={handleGenerateCode}
         disabled={!selectedMethod || loading?.identityGenerate2FACode === true}
         sx={{ width: '100%', mb: '12px' }}
        >
         {t('login.generateCode')}
        </Button>
       ) : (
        <TextField
         label={t('login.verificationCode')}
         value={code}
         onChange={handleCodeChange}
         sx={{
          width: '100%',
          mb: '12px',
         }}
         slotProps={{
          inputLabel: {
           shrink: code.length > 0,
          },
         }}
        />
       )}
      </>
     )}
     {errorMessage && (
      <Alert severity="error" sx={{ mb: 2 }}>
       {errorMessage}
      </Alert>
     )}
     <Button
      variant="contained"
      size="large"
      onClick={handle2FASubmit}
      disabled={
       !selectedMethod ||
       !code ||
       ((selectedMethod === 1 || selectedMethod === 2) && !codeGenerated) ||
       loading?.identityVerify2FA === true ||
       loading?.identityGenerate2FACode === true
      }
      sx={{ width: '100%', mb: { xs: 1, md: 1 } }}
     >
      {t('login.verify')}
     </Button>
     <Button variant="text" onClick={handleBackToLogin} sx={{ width: '100%' }}>
      {t('login.backToLogin')}
     </Button>
    </Box>
   )}
   {step === 0 && (
    <Box>
     <Typography sx={{ fontSize: 18, mb: '12px' }} onClick={handleDevLogin}>
      {t('login.getStarted')}
     </Typography>
     <Typography
      sx={{ fontSize: 14, mb: compactLoginDescriptionMargin ? '12px' : '28px' }}
     >
      {t('login.enterCredentials')}
     </Typography>
     {redirectedFromReset && (
      <Alert
       severity="info"
       icon={false}
       sx={{ mb: showForgotPasswordEmailSentInfo ? '12px' : '28px' }}
      >
       {t('login.passwordResetSuccess')}
      </Alert>
     )}
     {showForgotPasswordEmailSentInfo && (
      <Alert severity="info" icon={false} sx={{ mb: '28px' }}>
       {t('login.forgotPasswordEmailSentIfExists')}
      </Alert>
     )}
     <TextField
      {...emailRegister}
      label={t('login.email')}
      onChange={handleEmailChange}
      sx={{
       width: '100%',
       mb: '12px',
      }}
      //   slotProps={{
      //    inputLabel: {
      //     shrink: watch('email')?.length > 0,
      //    },
      //   }}
      error={!!errors.email}
      helperText={errors.email?.message?.toString() || ''}
     />
     <Box sx={{ position: 'relative', mb: { xs: 2, md: '24px' } }}>
      <TextField
       {...passwordRegister}
       type={passwordVisible ? 'text' : 'password'}
       label={t('login.password')}
       onChange={handlePasswordChange}
       sx={{
        width: '100%',
       }}
       //    slotProps={{
       //     inputLabel: {
       //      shrink: watch('password')?.length > 0,
       //     },
       //    }}
       error={!!errors.password}
       helperText={errors.password?.message?.toString() || ''}
      />
      <IconButton
       onClick={handleTogglePasswordVisibility}
       sx={{
        position: 'absolute',
        top: '23px',
        right: '12px',
        transform: 'translateY(-50%)',
        opacity: 0.5,
        color: (theme: Theme) => theme.palette.kemek.darkGray,
       }}
      >
       {passwordVisible ? <IconHide /> : <IconShow />}
      </IconButton>
     </Box>
     {errorMessage && (
      <Alert severity="error" sx={{ mb: 2 }}>
       {errorMessage}
      </Alert>
     )}
     <Box
      sx={{
       display: 'flex',
       alignItems: { xs: 'center', md: 'flex-start', lg: 'center' },
       flexDirection: { md: 'column', lg: 'row' },
       justifyContent: 'space-between',
       gap: 1,
       mb: { xs: 2, md: 3 },
       fontSize: 12,
      }}
     >
      <FormControlLabel
       sx={{}}
       label={
        <Typography sx={{ fontSize: 12 }}>{t('login.rememberMe')}</Typography>
       }
       control={<Checkbox checked color="primary" onChange={() => {}} />}
      />
      <LinkMui onClick={handleForgotPasswordClick}>
       {t('login.forgotPassword')}
      </LinkMui>
     </Box>

     <Button
      type="submit"
      variant="contained"
      size="large"
      sx={{ width: '100%', mb: { xs: 4, md: 5 } }}
      disabled={loading?.identityLogin || false}
     >
      {t('login.loginButton')}
     </Button>
     <Typography sx={{ fontSize: 14, mb: 3 }}>
      {t('login.useAnotherService')}
     </Typography>
     <Box
      sx={{
       width: '100%',
       display: 'flex',
       justifyContent: 'center',
       flexDirection: { xs: 'column', lg: 'row' },
       gap: 1,
      }}
     >
      <Button
       variant="contained"
       color="secondary"
       disableElevation
       startIcon={<GoogleIcon />}
       sx={{
        background: (theme: Theme) => theme.palette.kemek.lightBlue,
        color: (theme: Theme) => theme.palette.kemek.darkGray,
        fontSize: 10,
       }}
       onClick={() => handleLoginWith('google')}
      >
       {t('login.loginWithGoogle')}
      </Button>
      <Button
       variant="contained"
       color="secondary"
       disableElevation
       startIcon={<MicrosoftIcon />}
       sx={{
        background: (theme: Theme) => theme.palette.kemek.lightBlue,
        color: (theme: Theme) => theme.palette.kemek.darkGray,
        fontSize: 10,
       }}
       onClick={() => handleLoginWith('microsoft')}
      >
       {t('login.loginWithMicrosoft')}
      </Button>
     </Box>
    </Box>
   )}
   {step === 2 && (
    <Box>
     <Typography sx={{ fontSize: 18, mb: '12px' }}>
      {t('login.forgotPasswordTitle')}
     </Typography>
     <Typography sx={{ fontSize: 14, mb: '28px' }}>
      {t('login.forgotPasswordDescription')}
     </Typography>
     <TextField
      label={t('login.email')}
      value={forgotEmail}
      onChange={handleForgotEmailChange}
      sx={{
       width: '100%',
       mb: '12px',
      }}
      slotProps={{
       inputLabel: {
        shrink: forgotEmail.length > 0,
       },
      }}
     />
     {errorMessage && (
      <Alert severity="error" sx={{ mb: 2 }}>
       {errorMessage}
      </Alert>
     )}
     <Button
      type="submit"
      variant="contained"
      size="large"
      sx={{ width: '100%', mt: '12px', mb: 1 }}
      disabled={forgotPasswordLoading}
     >
      {t('login.sendCode')}
     </Button>
     <Button
      variant="text"
      onClick={handleBackToLogin}
      color="secondary"
      sx={{ width: '100%' }}
     >
      {t('login.backToLogin')}
     </Button>
    </Box>
   )}
  </Box>
 );
};

export default LoginForm;
